import React, { createContext, useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { columns } from "../pages/panel/PanelOrderV2/list-orders";
import { objectToForEach } from "../utils/objectToForEach";
import { OrderFilters } from "./queries/useOrdersV2";
import { setQueryParams, useQueryParams } from "./useQueryParams";

interface OrderPageTab {
  pageKey: string;
  page?: number;
  filters?: OrderFilters;
}

interface OrderPageContextType {
  tab: string;
  setTab: (tab: string) => void;
  pages: OrderPageTab[];
  setPages: (params: OrderPageTab) => void;
  currentPage?: OrderPageTab;
}

const OrderPageContext = createContext({} as OrderPageContextType);

export const OrderPageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const query = useQueryParams();
  const history = useHistory();

  const initialTab = query.get("tab") ?? "Todos";
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [pagesData, setPagesData] = useState<OrderPageTab[]>([]);

  const findCurrentPage = pagesData.find((p) => p.pageKey === currentTab);
  const currentPage = !!findCurrentPage ? findCurrentPage : getInitialPage();

  function onSetTab(tab: string) {
    setCurrentTab(tab);
    setQueryParams({
      data: { field: "tab", value: tab },
      type: "set",
      history,
    });

    const pageData = pagesData.find((p) => p.pageKey === tab);
    if (pageData) {
      applyPageDataToQueryParams(pageData);
    }
  }

  function onSetPageData(data: OrderPageTab) {
    const exist = pagesData.find((f) => f.pageKey === data.pageKey);
    const updated = exist
      ? pagesData.map((item) =>
          item.pageKey === data.pageKey ? { ...item, ...data } : item
        )
      : [...pagesData, data];

    setPagesData(updated);

    if (data.pageKey === currentTab) {
      applyPageDataToQueryParams(data);
    }
  }

  function applyPageDataToQueryParams(data: OrderPageTab) {
    const filterKeys = [
      "cod",
      "documentNumber",
      "codClient",
      "cnpj",
      "company_name",
      "rejection",
      "canceled",
      "status",
      "codSeller",
      "brand",
      "orderPeriod",
      "orderPresetDate",
      "orderSpecificDate",
      "orderStartDate",
      "orderEndDate",
      "billingOrderPeriod",
      "billingOrderPresetDate",
      "billingOrderSpecificDate",
      "billingOrderStartDate",
      "billingOrderEndDate",
    ];

    filterKeys.forEach((key) => {
      setQueryParams({ data: { field: key }, type: "delete", history });
    });

    setQueryParams({
      data: {
        field: "page",
        value: data.page?.toString() ?? "1",
      },
      type: "set",
      history,
    });

    objectToForEach(data.filters ?? {}, (key, value) => {
      if (value) {
        setQueryParams({
          data: {
            field: String(key),
            value: String(value),
          },
          type: "set",
          history,
        });
      }
    });
  }

  function getInitialPage() {
    if (pagesData.length >= 1) return undefined;

    let filtersValueQueryParams: any = {};
    query.forEach((value, key) => {
      const isFilter = columns.some((f) => f.name === key);
      if (isFilter) {
        filtersValueQueryParams[key] = value;
      }
    });
    const pageFromUrl = Number(query.get("page")) || 1;

    const initialPage = {
      pageKey: initialTab,
      page: pageFromUrl,
      filters: filtersValueQueryParams,
    };

    onSetPageData(initialPage);

    return initialPage;
  }

  const value = useMemo(
    () => ({
      tab: currentTab,
      setTab: onSetTab,
      pages: pagesData,
      setPages: onSetPageData,
      currentPage,
    }),
    // eslint-disable-next-line
    [currentTab, pagesData, currentPage]
  );

  return (
    <OrderPageContext.Provider value={value}>
      {children}
    </OrderPageContext.Provider>
  );
};

export const useOrderPages = () => useContext(OrderPageContext);
